import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  effect,
  input,
} from '@angular/core';
import { Message } from '../chatbot-core/message/message.model';

import { Subscription } from 'rxjs';
import { DisableCopyDirective } from '../shared/directives/disable-copy.directive';
import { SabisChatbotInputComponent } from './input/input.component';
import { IMessageService } from '../chatbot-core/message/message.service.interface';
import {
  ChatbotInitialConnection,
  ChatbotService,
  SessionsQuotaStatus,
} from '../chatbot-core/chatbot.service';
import { PromptQuotaStatus } from '../chatbot-core/chatbot-api/prompt-quota-status.model';
import { AppNetworkService } from '../shared/network/app-network.service';
import { ChatbotConfigService } from '../chatbot-core/chatbot-config/chatbot-config.service';
import { ChatbotHistoryService } from './history/chatbot-history.service';

import { SabisChatbotBotMessageComponent } from './messages/bot-message/bot-message.component';
import { SabisChatbotUserMessageComponent } from './messages/user-message/user-message.component';
import { ChatbotMode } from '../chatbot-core/chatbot-api/chatbot-mode.model';
import { MessageHistoryDTO } from './history/chatbot-history-api.service';
import { v4 as uuidv4 } from 'uuid';
import { RetryButtonComponent } from '../shared/components/retry-button/retry-button.component';
import { ChatbotConfig, ChatbotPopupService } from '../../public-api';

@Component({
  selector: 'sabis-chatbot',
  imports: [
    SabisChatbotInputComponent,
    DisableCopyDirective,
    SabisChatbotBotMessageComponent,
    SabisChatbotUserMessageComponent,
    RetryButtonComponent
  ],
  templateUrl: './sabis-chatbot.component.html',
  styleUrl: './sabis-chatbot.component.scss'
})
export class SabisChatbotComponent implements OnInit, OnDestroy, AfterViewInit {
  public messages: Message[] = [];
  public connectionStatus: string = '';
  /** specify if the retry button should be visible */
  public shouldShowRetryButton: boolean = false;
  public isConnectionEstablished: boolean = false;
  public maxChars: number = 600;
  public sessionQuotaStatus: SessionsQuotaStatus | null = null;
  public promptQuotaStatus: PromptQuotaStatus | null = null;
  public isOutOfSessions: boolean = false;
  public isOutOfPrompts: boolean = false;
  public isNetworkOnline: boolean = false;
  public userFirstName: string = '';
  public canAddEquation: boolean = true;
  public disclaimerMessage: string = "I'm your learning AI companion. Please don't share any personal, sensitive, or confidential information with me. All my answers are inspired by your E-books. Always verify them!";
  public isChatbotModeActive: boolean = true;
  public chatbotModes: ChatbotMode[] = [];
  /** True while loading messages for a session selected from history. */
  public isLoadingHistoryMessages: boolean = false;
  /** True when loading history messages has failed. */
  public isHistoryLoadingFailed: boolean = false;

  /** When set by parent (e.g. after user selects a session from history), load that session's messages and show them. */
  public selectedHistorySessionId = input<number | null>(null);

  public llmSessionId: string | null = null;


  /** Returns true if there's an active session (session_id is not null) */
  public get hasActiveSession(): boolean {
    return this._chatbotService.getActiveSessionId() !== null;
  }

  @ViewChild('messagesContainer') private _messagesContainer!: ElementRef;
  @ViewChild('inputComponent') private _inputComponent!: SabisChatbotInputComponent;
  @ViewChildren('userMessage') private _userMessages!: QueryList<SabisChatbotUserMessageComponent>;

  /** Emits the active session id when connection is established, or null when disconnected. */
  @Output() connectionEstablished = new EventEmitter<string | null>();
  @Input() isSessionIdVisible: boolean = false;
  @Output() contentClick = new EventEmitter<{ href: string; category: string | null }>();


  private _lastLoadedHistorySessionId: number | null = null;

  private _autoScrollEnabled = true;
  private _messageSub: Subscription = new Subscription();
  private _clearSub: Subscription = new Subscription();
  private _sessionQuotaEventSub: Subscription = new Subscription();
  private _promptQuotaEventSub: Subscription = new Subscription();
  private _connectionSubscription: Subscription = new Subscription();
  private _networkEventSub: Subscription = new Subscription();
  private _getSessionSubscription: Subscription = new Subscription();
  private _isScrollEventActivated: boolean = false;

  private _chatInProgressEventSub: Subscription = new Subscription();
  private _isChatInProgress: boolean = false;

  constructor(
    @Inject('MessageService')
    private _messageService: IMessageService,
    private _chatbotService: ChatbotService,
    private _appNetworkService: AppNetworkService,
    private _chatbotConfigService: ChatbotConfigService,
    private _historyService: ChatbotHistoryService,
    private _chatbotPopupService: ChatbotPopupService
  ) {
    // React to selectedHistorySessionId changes
    effect(() => {
      const sessionId = this.selectedHistorySessionId();
      if (sessionId && sessionId !== this._lastLoadedHistorySessionId) {
        this._lastLoadedHistorySessionId = sessionId;
        this.loadHistorySession(sessionId);
      }
    });
  }

  ngOnInit(): void {
    this.startConnectionToChatbot();

    // listen to network status: disable New Chat if not connected
    this._networkEventSub = this._appNetworkService.online$.subscribe(
      (isOnline) => {
        this.isNetworkOnline = isOnline;
      }
    );

    this._messageSub = this._messageService.messages$.subscribe((messages) => {
      this.messages = messages;
      if (!this._isScrollEventActivated) {
        this.ngAfterViewInit();
      }
      setTimeout(() => this.scrollToBottom(), 0);
    });

    this._chatInProgressEventSub = this._chatbotService.isChatInProgress$.subscribe((isChatInProgress) => {
      this._isChatInProgress = isChatInProgress;
    });

    // listen to changes in remaining sessions quota
    this._sessionQuotaEventSub = this._chatbotService.sessionsQuota$.subscribe(
      (status) => {
        this.sessionQuotaStatus = status;
        this.isOutOfSessions = status.limitReached;
      }
    );

    // listen to changes in remaining prompts quota
    this._promptQuotaEventSub = this._chatbotService.promptQuota$.subscribe(
      (status) => {
        this.promptQuotaStatus = status;
        this.isOutOfPrompts = status.limitReached;
      }
    );

    setTimeout(() => this.scrollToBottom(), 0);

    this.userFirstName = this._chatbotConfigService.getConfig()!.userFirstName;
  }

  ngAfterViewInit(): void {
    if (!this._messagesContainer) return;

    this._isScrollEventActivated = true;
    const container = this._messagesContainer.nativeElement;

    const updateAutoScroll = () => {
      const threshold = 50;
      const position = container.scrollTop + container.clientHeight;
      const height = container.scrollHeight;
      this._autoScrollEnabled = height - position <= threshold;
    };

    container.addEventListener('scroll', updateAutoScroll);

    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (['PageUp', 'PageDown', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        setTimeout(updateAutoScroll, 50);
      }
    });

    container.addEventListener('touchend', () => {
      setTimeout(updateAutoScroll, 50);
    });

    window.addEventListener('resize', () => {
      this.scrollToBottom();
    });
    this.userFirstName = this._chatbotConfigService.getConfig()!.userFirstName;
  }

  public onSendMessage(): void {
    this._autoScrollEnabled = true;
  }

  private scrollToBottom(): void {
    if (this._autoScrollEnabled && this._messagesContainer?.nativeElement) {
      const container = this._messagesContainer.nativeElement;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  public onCloseChatbot(): void {
    this._messageSub.unsubscribe();
    this._chatbotService.closeActiveSession();
    this.clearMessages();
  }

  private onConnectionStatusUpdate(cnxStatus: ChatbotInitialConnection): void {
    this.connectionStatus = cnxStatus.status.statusMessage;
    this.shouldShowRetryButton = cnxStatus.status.canRetry;

    this.isConnectionEstablished = cnxStatus.isConnected;
    this.maxChars = cnxStatus.maxChars;
    this.canAddEquation = cnxStatus.canAddEquation;
    this.disclaimerMessage = cnxStatus.disclaimerMessage;
    this.isChatbotModeActive = cnxStatus.isChatbotModeActive;
    this.chatbotModes = cnxStatus.chatbotModes;

    // connection either established with session or without session (quota exceeded)
    if (this.isConnectionEstablished) {
      this.llmSessionId = this._chatbotService.getActiveSessionId();
      this.connectionEstablished.emit(this.llmSessionId);
      this._inputComponent.setFocus();
    }
  }

  public startConnectionToChatbot(): void {
    this._connectionSubscription.unsubscribe();
    this._connectionSubscription = this._chatbotService
      .establishConnection()
      .subscribe(connectionStatus => {
        this.onConnectionStatusUpdate(connectionStatus);
      });
  }

  public onNewChatStarted(): void {
    this.clearMessages();
  }

  private clearMessages(): void {
    this._isScrollEventActivated = false;
    this.promptQuotaStatus = null;
    this._messageService.clearMessages();
    this._inputComponent.onNewChat();
  }

  /**
   * Loads a session selected from history: shows loading, fetches session (with messages),
   * maps to Message[], fills the chat list, and sets it as the active session.
   */
  private loadHistorySession(sessionId: number): void {
    this.isLoadingHistoryMessages = true;
    this.isHistoryLoadingFailed = false;
    this._getSessionSubscription.unsubscribe();
    this._getSessionSubscription = this._historyService.getSession(sessionId).subscribe({
      next: (session) => {
        const messages = this.mapMessageHistoryToMessages(session.messages);
        this._messageService.clearMessages();
        messages.forEach((msg) => this._messageService.pushMessage(msg));
        this._chatbotService.loadSession(session.llmSessionId, session.promptsQuota);
        this.isLoadingHistoryMessages = false;
        setTimeout(() => this.scrollToBottom(), 0);
        this.llmSessionId = session.llmSessionId;
      },
      error: (err) => {
        console.error('Failed to load history session:', err);
        this.isLoadingHistoryMessages = false;
        this.isHistoryLoadingFailed = true;
      },
    });
  }

  /** Maps API message history rows into UI Message objects (user + optional chatbot reply per row). */
  private mapMessageHistoryToMessages(rows: MessageHistoryDTO[]): Message[] {
    const messages: Message[] = [];
    for (const row of rows) {
      const userDate = row.date;
      const userMsg: Message = {
        id: uuidv4(),
        sender: 'user',
        content: row.userMessage,
        timestamp: userDate,
      };
      messages.push(userMsg);
      if (row.reply != null || row.isReplyInterrupted) {
        messages.push({
          id: uuidv4(),
          sender: 'chatbot',
          content: row.reply ?? '',
          timestamp: userDate,
          parentMessage: userMsg,
          isChatbotResponseInterrupted: row.isReplyInterrupted
        });
      }
    }
    return messages;
  }

  public onRegenerateMessage(message: Message): void {
    if (!message.parentMessage) {
      console.warn('parent message cannot be null');
      return;
    }

    // Check conditions before executing
    if (
      !this.isNetworkOnline ||
      !this.isConnectionEstablished ||
      this._isChatInProgress ||
      this.isOutOfPrompts
    ) {
      console.warn('Cannot regenerate response: conditions not met');
      return;
    }

    // Delete both parent message and current message
    this._messageService.deleteMessage(message.parentMessage.id);
    this._messageService.deleteMessage(message.id);

    // send message
    this._inputComponent.sendUserMessage(message.parentMessage.content);
  }

  public onResendMessage(message: Message): void {
    // Check conditions before executing
    if (
      !this.isNetworkOnline ||
      !this.isConnectionEstablished ||
      this._isChatInProgress ||
      this.isOutOfPrompts
    ) {
      console.warn('Cannot resend message: conditions not met');
      return;
    }

    // Delete the message
    this._messageService.deleteMessage(message.id);

    // send message
    this._inputComponent.sendUserMessage(message.content);
  }

  public onEditMessage(message: Message): void {
    // Cancel any existing edit before starting a new one
    this._userMessages.forEach(component => {
      if (component.isEditingMessage) {
        component.cancelEdit();
      }
    });
    this._inputComponent.editMessage(message.content, message.id);
  }

  public onCancelEdit(messageId: string | null): void {
    if (messageId) {
      const editingComponent = this._userMessages.find(
        component => component.message.id === messageId
      );
      if (editingComponent) {
        editingComponent.cancelEdit();
      }
    }
  }

  /**
   * Retry loading the last selected history session.
   */
  public retryLoadingHistory(): void {
    if (this._lastLoadedHistorySessionId !== null) {
      this.loadHistorySession(this._lastLoadedHistorySessionId);
    }
  }

  public onContentClick(event: { href: string; category: string | null }): void {
    this.contentClick.emit(event);
  }

  ngOnDestroy(): void {
    this._messageSub.unsubscribe();
    this._clearSub.unsubscribe();
    this._sessionQuotaEventSub.unsubscribe();
    this._promptQuotaEventSub.unsubscribe();
    this._connectionSubscription.unsubscribe();
    this._networkEventSub.unsubscribe();
    this._chatInProgressEventSub.unsubscribe();
    this._getSessionSubscription.unsubscribe();
  }
}
